import React, { useState } from 'react';
import { Button, Dropdown, Input, Space } from 'antd';
import { useNavigate } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import { BrandMark } from '../brand/Brand';
import TermText from './TermText';
import { buildActionMenu } from '../common/actionMenu';
import { CHECK, COLLECTION_ICON, ENVELOPE, KEBAB, OPEN, SLASH_CIRCLE, TRASH } from '../icons';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { normalizeId } from '../../utils/ids';
import { linkedEntryId } from '../../hooks/useLinkedEntry';
import { describeAlertState, describeWaitingFor, isResolvedAlert } from '../../utils/notificationCopy';
import { linkNoticePath } from '../../utils/collectionLinks';
import { DRAFT_FILES_NOTICE_TITLE, draftNoticeCollectionId, draftNoticePath, draftNoticeText, isDraftFilesNotice } from '../../utils/draftNotice';
import { FILE_FLAGGED_NOTICE_TITLE, fileFlaggedCollectionId, fileFlaggedPath, fileFlaggedText, isFileFlaggedNotice } from '../../utils/fileFlaggedNotice';
import useCollectionIdentity from '../../hooks/useCollectionIdentity';
import useCollectionOffersCard from '../../hooks/useCollectionOffersCard';

/**
 * One row of the inbox: what happened, which record it happened to, what is
 * still owed, and the buttons that settle it.
 *
 * Two registries govern what this row will render, and BOTH fail closed:
 *
 * - **CTA types** (`ctaTarget`) — an unrecognized one renders NOTHING rather
 *   than a dead button, because a newer server may write CTA kinds this build
 *   has never heard of.
 * - **Response kinds** (`RESPONSE_KINDS`) — the same discipline for the second
 *   registry. A response is a POST with consequences (it can close an alert
 *   and stop a ladder), so a kind this build cannot perform must not render a
 *   button that pretends it can.
 *
 * The record the row is ABOUT fails closed for a third reason, and a different
 * one: not that this build might not understand it, but that this build cannot
 * check it. See `subjectOf`.
 *
 * Nothing here is ever recalled or struck through. A row that resolves turns
 * in place — the dot becomes a check, the band changes tense from "Waiting
 * for" to "Met" — and demotes to Earlier on the next fetch. Somebody acted;
 * deleting the evidence would be a lie about what happened.
 */

// Three at most, so a row stays a row. The author's order is kept: they wrote
// the ladder, and the first button is the one they meant first.
const MAX_RESPONSES = 3;

// The response kinds the signed-in inbox can perform. `open-item` and
// `create-item` are the CTA registry's verbs and arrive as `ctas` — a
// response is something you SAY, and these four are what the POST accepts.
const RESPONSE_KINDS = new Set( [ 'acknowledge', 'resolve', 'snooze', 'answer' ] );

/**
 * One CTA's destination, or null when this build does not know the type.
 * Exported (and re-exported from the drawer) for the tests that assert the
 * create-item URL shape — it is the contract between the notify action and
 * the item form's prefill.
 */
export function ctaTarget( cta, notification ) {
	// A PLATFORM NOTICE's button (keepr-api utils/systemNotify.js): an in-app
	// path, nothing else — the server keeps only paths starting with one '/',
	// and so does this.
	if ( cta?.type === 'open-path' ) {
		const path = typeof cta.path === 'string' ? cta.path : '';
		// '//host' and '/\\host' both read as another origin in a browser.
		return /^\/(?![/\\])/.test( path ) && ! path.includes( '\\' ) ? path : null;
	}

	const collectionId = normalizeId( cta?.collection_id ) || normalizeId( notification?.collection_id );
	if ( ! collectionId ) return null;

	if ( cta?.type === 'create-item' ) {
		const cardId = normalizeId( cta.card_id );
		const notificationId = normalizeId( notification?._id );
		if ( ! cardId || ! notificationId ) return null;
		return `/collections/${ collectionId }/items/new/${ cardId }?notification=${ notificationId }`;
	}

	if ( cta?.type === 'open-item' ) {
		const itemId = normalizeId( cta.item_id );
		if ( ! itemId ) return null;
		// …at one entry of a repeating group, when the notice is about one
		// (KPR-836: a reminder about an entry's date, or a rule that watches
		// entries): KPR-690's `#entry-<id>` anchor. Only an id keepr could
		// have issued reaches the address.
		const entryId = linkedEntryId( `#entry-${ cta.entry_id ?? '' }` );
		return `/collections/${ collectionId }/items/${ itemId }${ entryId ? `#entry-${ entryId }` : '' }`;
	}

	return null;
}

/**
 * THE SUBJECT, OR NOTHING — the one reading of the record a row is about.
 *
 * `subject.title` is composed on the SERVER and frozen at send time (keepr-api
 * `utils/notifications/openAlerts.js`: the item's `displayValue` as it read the
 * moment the obligation opened). Nothing on this side can resolve it, re-read
 * it, or tell a title that is still this reader's to see from one that stopped
 * being theirs — a grant revoked, an item turned private, a record deleted — in
 * the weeks a ladder can stay open. And a composed title is exactly the value
 * the omit rule names: it may carry another record's name inside it, because
 * a display template can nest a lookup (`utils/lookups.js`).
 *
 * So THE DISCLOSED TITLE IS THE BOUNDARY, the same way the public page's
 * pre-rendered title map is (`utils/lookups.js createTitleMapResolver`): the
 * server sends a title for the record this reader may still see and sends
 * none otherwise, and a missing one means *not yours to see*, never *not
 * loaded yet*. A row with no title therefore knows NO subject at all — no
 * badge, and no door either. An id is not a reading of anything
 * (`resolveLookupText`), and a door built from one still says that a record
 * by that id sits in this collection.
 *
 * ⚠ BOTH READERS COME HERE, and that is the point. The badge gated on the
 * title while the row menu gated on the id, so a withheld title silenced the
 * badge and left "Open item" pointing at the very record it was withheld for.
 */
export function subjectOf( notification ) {
	const subject = notification?.subject || null;
	const title = typeof subject?.title === 'string' ? subject.title.trim() : '';
	if ( ! title ) return null;
	const collectionId = normalizeId( notification?.collection_id );
	const itemId = normalizeId( subject.item_id );
	return {
		title,
		kicker: subject.kicker,
		// The collection the record lives in: the inbox gathers every
		// collection's, so its badge is the qualified one — named from what
		// this reader can already read (hooks/useCollectionIdentity), never
		// from the notification.
		collectionId: collectionId || null,
		// The wire sends no icon or colour with the subject, so the badge wears
		// its own fallback tile — and picks the card's up for free the day the
		// API starts sending one.
		card: subject.card || null,
		path: collectionId && itemId ? `/collections/${ collectionId }/items/${ itemId }` : null,
	};
}

export default function InboxRow( {
	notification,
	currentUserId,
	onMarkRead,
	onMarkUnread,
	onDismiss,
	onRespond,
	onClose,
} ) {

	const navigate = useNavigate();
	// The open free-text response, if one is being typed. Null the rest of
	// the time: the textarea is a response, not a permanent field.
	const [ answering, setAnswering ] = useState( null );
	const [ answer, setAnswer ] = useState( '' );

	const id = normalizeId( notification?._id );
	const unread = ! notification?.readAt;
	const resolved = isResolvedAlert( notification );
	// The subject the API disclosed, or null — see `subjectOf`. Everything the
	// row says about the record it is about hangs off this ONE answer.
	const subject = subjectOf( notification );

	const waiting = describeWaitingFor( notification );
	const stateLine = describeAlertState( notification, { currentUserId } );

	// A COLLECTION LINK notice (`kind: 'collection-link'` — a request, an
	// answer, a revoke) is acted on in the notified collection's Sharing tab.
	// The server's first button already opens it; the door is added here only
	// when a row does not carry one, and the ⋯ menu always has it.
	const sharingPath = linkNoticePath( notification );
	// The THREE-DAY NOTICE about a draft's files (`kind: 'draft.files_expiring'`,
	// PR 10b): in plain words — the server's, else utils/draftNotice's — and
	// always with a door to the draft.
	const draftNotice = isDraftFilesNotice( notification );
	// A FLAGGED FILE (`kind: 'file-flagged'`, PR 10e): told once, in plain
	// words, with a door to the item that holds it.
	const flaggedNotice = isFileFlaggedNotice( notification );
	const noticeCollectionId = draftNotice ? draftNoticeCollectionId( notification ) : ( flaggedNotice ? fileFlaggedCollectionId( notification ) : null );
	const noticeCollection = useCollectionIdentity( noticeCollectionId, { fetch: draftNotice || flaggedNotice } );
	const draftPath = draftNotice ? draftNoticePath( notification ) : null;
	const flaggedPath = flaggedNotice ? fileFlaggedPath( notification ) : null;
	// A create-item button names a card the collection may not hold (a global
	// notification's response can name another global card, KPR-237 G13): it is
	// not offered there. Unknown while the collection loads, it stays.
	const offersCard = useCollectionOffersCard( notification?.collection_id, ( Array.isArray( notification?.ctas ) ? notification.ctas : [] ).some( cta => cta?.type === 'create-item' ) );
	const serverCtas = ( Array.isArray( notification?.ctas ) ? notification.ctas : [] )
		.map( cta => ( { cta, target: ctaTarget( cta, notification ) } ) )
		.filter( entry => entry.target )
		.filter( entry => entry.cta?.type !== 'create-item' || offersCard( entry.cta.card_id ) );
	const ctas = [
		...serverCtas,
		...( sharingPath && ! serverCtas.some( entry => entry.target === sharingPath )
			? [ { cta: { label: 'Open sharing' }, target: sharingPath } ]
			: [] ),
		...( draftPath && ! serverCtas.some( entry => String( entry.target ).startsWith( '/drafts' ) )
			? [ { cta: { label: 'Open draft' }, target: draftPath } ]
			: [] ),
		...( flaggedPath && ! serverCtas.some( entry => entry.target === flaggedPath )
			? [ { cta: { label: 'Open item' }, target: flaggedPath } ]
			: [] ),
	];
	const collectionName = noticeCollection?.name || '';
	let fallbackTitle = 'Notification';
	let fallbackBody = null;
	if ( draftNotice ) {
		fallbackTitle = DRAFT_FILES_NOTICE_TITLE;
		fallbackBody = draftNoticeText( notification, { collectionName } );
	} else if ( flaggedNotice ) {
		fallbackTitle = FILE_FLAGGED_NOTICE_TITLE;
		fallbackBody = fileFlaggedText( notification, { collectionName } );
	}
	const title = notification?.title || fallbackTitle;
	const body = notification?.body || fallbackBody;

	// Answered rows show the resolution line (in the band) instead of the
	// buttons — never a row of disabled ones, which reads as broken rather
	// than as finished.
	const responses = notification?.respondedBy
		? []
		: ( Array.isArray( notification?.responses ) ? notification.responses : [] )
			.filter( response => response?.key && RESPONSE_KINDS.has( response.kind ) )
			.slice( 0, MAX_RESPONSES );

	// The one-filled-primary rule: a filled button is an ANSWER the author
	// marked as the affirmative one, and it only reads as that beside exactly
	// one alternative. Three buttons are a menu, and a menu has no default.
	const primaryKey = responses.length === 2
		? ( responses.find( response => response.style === 'primary' )?.key || null )
		: null;

	/**
	 * Leaving the drawer: mark the row read, go, and close behind you. The
	 * row itself marks read too, so a button stops the click from bubbling —
	 * it navigates away, and a double mark-read would fight the rollback.
	 */
	const go = ( event, target ) => {
		event.stopPropagation();
		onMarkRead?.( id );
		if ( target ) navigate( target );
		onClose?.();
	};

	const respond = ( event, response ) => {
		event.stopPropagation();
		if ( response.kind === 'answer' ) {
			setAnswering( response.key );
			setAnswer( '' );
			return;
		}
		onRespond?.( id, response.key );
	};

	const sendAnswer = ( event ) => {
		event.stopPropagation();
		const text = answer.trim();
		if ( ! text ) return;
		onRespond?.( id, answering, { text } );
		setAnswering( null );
		setAnswer( '' );
	};

	const subjectPath = subject?.path || null;
	const collectionId = normalizeId( notification?.collection_id );
	// The thing shouting at you offers the door to its own settings — by its
	// CATALOGUE KEY and nothing else. The account list matches a key exactly or
	// a DEFINITION's name loosely, and it holds no rendered titles, so a title
	// here matched nothing; what it did do was put this message's own text —
	// which can quote a value dot-walked out of a record — into the address bar
	// and the browser's history. A row with no key has no catalogue row behind
	// it, and offers no mute rather than a search for its own prose.
	const settingsTerm = notification?.key || '';

	const menu = buildActionMenu( [
		subjectPath ? {
			key: 'open-item',
			group: 'navigate',
			icon: <OPEN />,
			label: 'Open item',
			onClick: () => { onMarkRead?.( id ); navigate( subjectPath ); onClose?.(); },
		} : null,
		sharingPath ? {
			key: 'sharing',
			group: 'navigate',
			glyph: 'settings',
			label: 'Go to linked collections',
			onClick: () => { onMarkRead?.( id ); navigate( sharingPath ); onClose?.(); },
		} : null,
		collectionId ? {
			key: 'collection',
			group: 'navigate',
			icon: <COLLECTION_ICON />,
			label: 'Go to collection',
			onClick: () => { onMarkRead?.( id ); navigate( `/collections/${ collectionId }` ); onClose?.(); },
		} : null,
		! unread ? {
			key: 'unread',
			group: 'record',
			icon: <ENVELOPE />,
			label: 'Mark unread',
			onClick: () => onMarkUnread?.( id ),
		} : null,
		settingsTerm ? {
			key: 'mute',
			group: 'record',
			icon: <SLASH_CIRCLE />,
			label: 'Mute this notification…',
			onClick: () => { navigate( `/account/notifications?find=${ encodeURIComponent( settingsTerm ) }` ); onClose?.(); },
		} : null,
		{
			key: 'dismiss',
			group: 'destructive',
			icon: <TRASH />,
			label: 'Dismiss',
			// It removes the ROW, never the alert: the obligation behind it is
			// shared, and tidying an inbox must not close it for the shift.
			title: 'Removes it from your inbox. Anything it is waiting for stays open.',
			onClick: () => onDismiss?.( id ),
		},
	].filter( Boolean ) );

	const classes = [
		'inbox-row',
		unread ? 'inbox-row--unread' : '',
		resolved ? 'inbox-row--resolved' : '',
	].filter( Boolean ).join( ' ' );

	return (
		<li className={classes} onClick={() => onMarkRead?.( id )}>

			<div className="inbox-row__head">
				{resolved ? (
					<CHECK className="inbox-row__check" aria-label="Resolved" role="img" />
				) : (
					<span
						className={`inbox-row__dot${ unread ? ' inbox-row__dot--unread' : '' }`}
						aria-label={unread ? 'Unread' : undefined}
						role={unread ? 'img' : undefined}
					/>
				)}
				{/* A platform notice speaks as keepr, not as a collection's rule. */}
				{notification?.origin === 'system' ? (
					<BrandMark size={16} className="inbox-row__origin" title="From keepr" />
				) : null}
				<span className="inbox-row__title">{title}</span>
				<span className="inbox-row__time" title={formatDisplayDateTime( notification?.createdAt )}>
					{formatRelativeToNow( notification?.createdAt )}
				</span>
				{menu ? (
					<Dropdown trigger={[ 'click' ]} placement="bottomRight" menu={menu}>
						<Button
							size="small"
							icon={<KEBAB />}
							onClick={event => event.stopPropagation()}
							aria-label={`Actions for ${ notification?.title || 'this notification' }`}
						/>
					</Dropdown>
				) : null}
			</div>

			{body ? <p className="inbox-row__body">{body}</p> : null}

			{subject ? (
				// The door to the record this is ABOUT, which the inbox has never
				// offered before: the badge carries the card's name over the
				// item's title, exactly as it does in every other list. It draws
				// only what `subjectOf` disclosed — never an id, never a
				// placeholder standing in for a title the server withheld.
				<RecordBadge
					kind="item"
					size="sm"
					record={subject.card}
					kicker={subject.kicker}
					title={subject.title}
					collection={subject.collectionId}
					to={subjectPath || undefined}
					// A real link (middle-click, open in a new tab), which still
					// marks the row read and closes the drawer behind it.
					onClick={subjectPath ? event => { event.stopPropagation(); onMarkRead?.( id ); onClose?.(); } : undefined}
					className="inbox-row__subject"
				/>
			) : null}

			{/* Both lines are the API's own sentences (`utils/notifications/
			    describe.js`), and it marks the element and card names inside
			    them — `waitingFor.summary`, the rung's `nextAudience`, the
			    resolution's summary. Printed verbatim a reader would get literal
			    asterisks, so both come through the same convention every other
			    generated sentence in the product does. */}
			{waiting || stateLine ? (
				<div className={`inbox-row__band${ resolved ? ' inbox-row__band--met' : '' }`}>
					{waiting ? (
						<p className="inbox-row__waiting">
							<span className="inbox-row__waiting-label">{waiting.label}:</span>
							{' '}<TermText text={waiting.summary} termClassName="inbox-row__term" />
						</p>
					) : null}
					{stateLine ? (
						<p className="inbox-row__state">
							<TermText text={stateLine} termClassName="inbox-row__term" />
						</p>
					) : null}
				</div>
			) : null}

			{responses.length ? (
				<Space size={8} wrap className="inbox-row__responses">
					{responses.map( response => (
						<Button
							key={response.key}
							size="small"
							type={response.key === primaryKey ? 'primary' : 'default'}
							onClick={event => respond( event, response )}
						>
							{response.label || 'Respond'}
						</Button>
					) )}
				</Space>
			) : null}

			{answering ? (
				// In the drawer, never a modal: an answer is two lines, and a
				// dialog over the list would hide the thing being answered.
				<div className="inbox-row__answer" onClick={event => event.stopPropagation()}>
					<Input.TextArea
						rows={2}
						value={answer}
						autoFocus
						placeholder="Your answer"
						aria-label="Your answer"
						onChange={event => setAnswer( event.target.value )}
					/>
					<Space size={8}>
						<Button size="small" type="primary" disabled={! answer.trim()} onClick={sendAnswer}>Send</Button>
						<Button size="small" onClick={() => { setAnswering( null ); setAnswer( '' ); }}>Cancel</Button>
					</Space>
				</div>
			) : null}

			{ctas.length ? (
				<Space size={8} wrap className="inbox-row__ctas">
					{ctas.map( ( { cta, target }, index ) => (
						<Button
							key={`${ id }-cta-${ index }`}
							size="small"
							onClick={event => go( event, target )}
						>
							{cta.label || 'Open'}
						</Button>
					) )}
				</Space>
			) : null}

		</li>
	);
}
